export type PlanTier = 'free' | 'team' | 'growth' | 'enterprise';

/** Normalize legacy backend plan values to the current 4-tier model. */
export function normalizePlanTier(plan: string): PlanTier {
  if (plan === 'pro') return 'growth';
  return plan as PlanTier;
}

export interface PlanFeatures {
  rbac_policies: boolean;
  abac_policies: boolean;
  policy_studio: boolean;
  policy_versioning: boolean;
  multi_connector: boolean;
  advanced_analytics: boolean;
  rebac_policies: boolean;
  grounded_answers: boolean;
  vendor_iam: boolean;
  access_simulator: boolean;
  audit_export: boolean;
  sso_scim: boolean;
  mcp_server: boolean;
  siem_export: boolean;
  content_ref_mode: boolean;
  custom_kms: boolean;
  private_data_plane: boolean;
  batch_ingestion: boolean;
  async_ingestion: boolean;
  source_connectors: boolean;
  help_assistant: boolean;
}

export interface PlanLimits {
  secured_retrievals: number | null;
  connectors: number | null;
  identity_providers: number | null;
  principals: number | null;
  policies: number | null;
  team_members: number | null;
  api_keys: number | null;
  overage_price_cents: number;
}

export interface Plan {
  id: string;
  name: string;
  tier: PlanTier;
  price_monthly_cents: number;
  price_yearly_cents: number;
  features: PlanFeatures;
  limits: PlanLimits;
}

export interface UsageMetric {
  used: number;
  limit: number | null;
  overage?: number;
}

export interface Usage {
  period_start: string;
  period_end: string;
  secured_retrievals: UsageMetric & { overage: number };
  connectors: { used: number; limit: number | null };
  policies: { used: number; limit: number | null };
  /** Local (directly created) principals against the plan's principals limit. */
  principals: { used: number; limit: number | null };
  api_keys: { used: number; limit: number | null };
  identity_providers: { used: number; limit: number | null };
  team_members: { used: number; limit: number | null };
  estimated_overage_cents: number;
}

export interface Invoice {
  id: string;
  stripe_invoice_id: string;
  amount_cents: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  period_start: string | null;
  period_end: string | null;
  pdf_url: string;
  created_at: string;
}

export interface CheckoutRequest {
  plan_id: string;
  billing_period: 'monthly' | 'yearly';
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

export interface BillingPortalRequest {
  return_url?: string;
}

export interface BillingPortalResponse {
  portal_url: string;
}

export interface Subscription {
  id: string;
  plan_id: string;
  billing_period: 'monthly' | 'yearly';
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export type FeatureKey = keyof PlanFeatures;
